import Link from 'next/link'
import type { CSSProperties } from 'react'

/**
 * Shared pieces of the reference blog kit (public/pixelmojo-rebuild/
 * pixelmojo-blogs): the topic grouping rules and the `.bcard` card, as
 * pixelmojo-blog-generator.py writes them.
 */
export type BlogGroup = 'visibility' | 'building' | 'design'

const VIS =
  /visib|geo|aeo|search|llms|citation|monitor|grok|google|radar|knowledge|ghost|bots|readiness|brand-dis|preferred/
const DESIGN =
  /design|ux|market|brand|agency|agencies|growth|consumer|aesthetic|budget/

export const blogGroup = (cat: string): BlogGroup =>
  VIS.test(cat) ? 'visibility' : DESIGN.test(cat) ? 'design' : 'building'

const GCOL: Record<BlogGroup, string> = {
  visibility: 'var(--blue-light)',
  building: 'var(--pink)',
  design: 'var(--yellow)',
}

const GLABEL: Record<BlogGroup, string> = {
  visibility: 'AI visibility',
  building: 'Building AI',
  design: 'Design & growth',
}

export const catLabel = (cat: string) => cat.replace(/-/g, ' ')

/** The reference H1: the last two words in the serif face (4+ words). */
export function SerifTitle({ title }: { title: string }) {
  const words = title.split(' ')
  if (words.length < 4) return <>{title}</>
  return (
    <>
      {words.slice(0, -2).join(' ')}{' '}
      <span className='serif'>{words.slice(-2).join(' ')}</span>
    </>
  )
}

export interface BlogCardData {
  href: string
  title: string
  excerpt: string
  cat: string
  min: number
  /** An Arabic article (src/data/arabic-articles.ts). */
  lang?: 'ar'
}

/**
 * `hidden` server-renders the reference's own `.is-hidden` state: the
 * listing serves every card, but only the first page shows before the
 * listing script runs (and without JavaScript), matching its pager.
 */
export function BlogCard({
  p,
  n,
  hidden = false,
}: {
  p: BlogCardData
  n?: number
  hidden?: boolean
}) {
  const g = blogGroup(p.cat)
  return (
    <article
      className={hidden ? 'bcard is-hidden' : 'bcard'}
      data-g={g}
      data-cat={p.cat}
    >
      <div className='bcard__top'>
        <span>
          <i style={{ '--col': GCOL[g] } as CSSProperties} />
          {catLabel(p.cat)} · {p.min} min
        </span>
        {n ? <span>{String(n).padStart(2, '0')}</span> : null}
      </div>
      {p.lang === 'ar' ? (
        // A document navigation, never next/link: a client-side navigation
        // into /ar keeps this page's <html lang="en">.
        <>
          <h3 lang='ar' dir='rtl'>
            <a href={p.href} hrefLang='ar'>
              {p.title}
            </a>
          </h3>
          <p lang='ar' dir='rtl'>
            {p.excerpt}
          </p>
        </>
      ) : (
        <>
          <h3>
            <Link href={p.href}>{p.title}</Link>
          </h3>
          <p>{p.excerpt}</p>
        </>
      )}
      <div className='bcard__foot'>
        <span>{GLABEL[g]}</span>
        <span>{p.lang === 'ar' ? 'Read in Arabic →' : 'Read article →'}</span>
      </div>
    </article>
  )
}

/** Page 1 of the listing is /blogs; page N is the /blogs/page/N archive. */
export const blogPageHref = (n: number) =>
  n === 1 ? '/blogs' : `/blogs/page/${n}`

/**
 * The reference `.pager` (← 1 2 … N →), served as real links so every
 * archive page is reachable without JavaScript. On /blogs the listing
 * script takes it over for client-side paging and keeps the links (it
 * intercepts the clicks); /blogs/page/N serves it as is. Disabled ends
 * stay the reference's disabled buttons.
 */
export function BlogPager({
  page,
  pages,
  id,
}: {
  page: number
  pages: number
  id?: string
}) {
  return (
    <nav className='pager' id={id} aria-label='Pagination'>
      {pages > 1 ? (
        <>
          {page > 1 ? (
            <Link href={blogPageHref(page - 1)} aria-label='Previous page'>
              ←
            </Link>
          ) : (
            <button type='button' disabled aria-label='Previous page'>
              ←
            </button>
          )}
          {Array.from({ length: pages }, (_, i) => i + 1).map(n => (
            <Link
              key={n}
              href={blogPageHref(n)}
              aria-current={n === page ? 'page' : undefined}
            >
              {n}
            </Link>
          ))}
          {page < pages ? (
            <Link href={blogPageHref(page + 1)} aria-label='Next page'>
              →
            </Link>
          ) : (
            <button type='button' disabled aria-label='Next page'>
              →
            </button>
          )}
        </>
      ) : null}
    </nav>
  )
}

/** The accent CTA sheet that closes every blog page. */
export function BlogCta({
  ghost,
  secondary,
}: {
  ghost: string
  secondary: { href: string; label: string }
}) {
  return (
    <section
      className='sheet sheet--accent sheet--pad cta bpage'
      id='start'
      data-rail='Start'
      style={{ paddingBottom: 'calc(var(--section) + var(--radius-sheet))' }}
    >
      <div className='cta__ghost' aria-hidden='true'>
        {ghost}
      </div>
      <div className='container cta__inner'>
        <div data-reveal='' style={{ display: 'grid', gap: 22 }}>
          <span className='eyebrow'>Read enough? Check your own</span>
          <h2 className='h-display h2'>
            Does AI get <span className='serif'>your business?</span>
          </h2>
          <p className='lede' style={{ color: 'rgba(18,18,18,.72)' }}>
            Run Radar&apos;s six free technical checks on your domain, then
            follow the evidence behind each finding.
          </p>
        </div>
        <div className='hero__cta' data-reveal='fade'>
          <Link className='btn' href='/platform#free-audit'>
            Get My Free Snapshot{' '}
            <svg
              className='arrow'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.8'
            >
              <path d='M5 12h14M13 6l6 6-6 6' />
            </svg>
          </Link>
          <Link className='btn btn--ghost' href={secondary.href}>
            {secondary.label}
          </Link>
        </div>
      </div>
    </section>
  )
}
